// src/components/settings/EmailTemplatesSettings.js
import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Mail, RotateCcw, Save, Eye, EyeOff, ChevronDown } from 'lucide-react';
import { resolveDocTerminology } from '../../utils/quoteSchema';
import { escapeHtml } from '../../utils/emailEscape'; // s9preview-7a3d2f40 — same escaper as the server
import { canOrgTakeOnlinePayments, invoiceEmailWordingFor } from '../../utils/orgPayments'; // tmplpay-5f1d8b23

// ── Default templates ─────────────────────────────────────────────────────────
const DEFAULTS = {
    bookingConfirmation: {
        subject: `Your appointment is confirmed — {companyName}`,
        body: `Hi {customerName},\n\nYour appointment has been confirmed. Here are the details:\n\nService: {jobTitle}\nSchedule: {date} — {arrivalWindow}\n\nIf you have any questions, please don't hesitate to reach out.\n\nThank you for choosing {companyName}!`,
    },
    dayBeforeReminder: {
        subject: `Reminder: Your appointment is tomorrow — {companyName}`,
        body: `Hi {customerName},\n\nThis is a friendly reminder that your appointment is tomorrow.\n\nService: {jobTitle}\nSchedule: {date} — {arrivalWindow}\n\nSee you tomorrow!\n\n{companyName}`,
    },
    invoice: {
        subject: `Invoice #{invoiceNumber} from {companyName}`,
        body: `Hi {customerName},\n\nPlease find your invoice attached below. You can view and pay it online using the button above.\n\nTotal due: {total}\nDue date: {dueDate}\n\nThank you for your business!\n\n{companyName}`,
    },
    quote: {
        subject: `Quote #{quoteNumber} from {companyName}`,
        body: `Hi {customerName},\n\nPlease find your quote attached below. You can view, approve, or decline it online using the button above.\n\nTotal: {total}\n\nThank you for considering {companyName}!`,
    },
};

// tmplpay-5f1d8b23 — the invoice default the editor offers. An org that CAN take online payments
// keeps DEFAULTS.invoice exactly as it was. One that can't gets a body whose opening matches the
// server's own default for it ("Please find your invoice details below." + CANNOT_PAY
// .invoiceDefaultBodyLine, functions/index.js sendInvoiceEmail), so it never promises "pay online".
// Only the OFFERED default changes: a template the operator saved is loaded and sent as written.
export function makeInvoiceDefault(canPayOnline) {
    if (canPayOnline) return { ...DEFAULTS.invoice };
    return {
        subject: DEFAULTS.invoice.subject,
        body: `Hi {customerName},\n\nPlease find your invoice details below. ${invoiceEmailWordingFor(false).defaultBodyLine}\n\nTotal due: {total}\nDue date: {dueDate}\n\nThank you for your business!\n\n{companyName}`,
    };
}

// r1-p2-docterm-emailtmpl-a4d9f2c7 - Estimate-aware default quote template (org-default word; {tokens} stay literal)
function makeQuoteDefault(docWord) {
    return {
        subject: `${docWord} #{quoteNumber} from {companyName}`,
        body: `Hi {customerName},\n\nPlease find your ${docWord.toLowerCase()} attached below. You can view, approve, or decline it online using the button above.\n\nTotal: {total}\n\nThank you for considering {companyName}!`,
    };
}

// ── Placeholder definitions per tab ──────────────────────────────────────────
const PLACEHOLDERS = {
    bookingConfirmation: [
        { key: '{customerName}', label: 'Customer Name' },
        { key: '{companyName}',  label: 'Company Name' },
        { key: '{jobTitle}',     label: 'Job Title' },
        { key: '{date}',         label: 'Date' },
        { key: '{arrivalWindow}',label: 'Arrival Window' },
        { key: '{companyPhone}', label: 'Your Phone' },
        { key: '{companyEmail}', label: 'Your Email' },
    ],
    dayBeforeReminder: [
        { key: '{customerName}', label: 'Customer Name' },
        { key: '{companyName}',  label: 'Company Name' },
        { key: '{jobTitle}',     label: 'Job Title' },
        { key: '{date}',         label: 'Date' },
        { key: '{arrivalWindow}',label: 'Arrival Window' },
        { key: '{companyPhone}', label: 'Your Phone' },
        { key: '{companyEmail}', label: 'Your Email' },
    ],
    invoice: [
        { key: '{customerName}',  label: 'Customer Name' },
        { key: '{companyName}',   label: 'Company Name' },
        { key: '{invoiceNumber}', label: 'Invoice Number' },
        { key: '{total}',         label: 'Total' },
        { key: '{dueDate}',       label: 'Due Date' },
        { key: '{companyPhone}',  label: 'Your Phone' },
        { key: '{companyEmail}',  label: 'Your Email' },
    ],
    quote: [
        { key: '{customerName}', label: 'Customer Name' },
        { key: '{companyName}',  label: 'Company Name' },
        { key: '{quoteNumber}',  label: 'Quote Number' },
        { key: '{total}',        label: 'Total' },
        { key: '{companyPhone}', label: 'Your Phone' },
        { key: '{companyEmail}', label: 'Your Email' },
    ],
};

const TABS = [
    { key: 'bookingConfirmation', label: 'Booking Confirmation' },
    { key: 'dayBeforeReminder',   label: 'Day-Before Reminder' },
    { key: 'invoice',             label: 'Invoice' },
    { key: 'quote',               label: 'Quote' },
];

// ── Substitute placeholders with sample values ────────────────────────────────
function applyPlaceholders(text, companyName) {
    const samples = {
        '{customerName}':  'Jane Smith',
        '{companyName}':   companyName || 'Your Company',
        '{jobTitle}':      'Lawn Maintenance',
        '{date}':          'Wednesday, April 16, 2026',
        '{arrivalWindow}': 'Morning (8am–12pm)',
        '{companyPhone}':  '(555) 123-4567',
        '{companyEmail}':  'hello@yourcompany.com',
        '{invoiceNumber}': '0042',
        '{quoteNumber}':   '0018',
        '{total}':         '$185.00',
        '{dueDate}':       'May 1, 2026',
        '{portalLink}':    '#',
    };
    return Object.entries(samples).reduce(
        (str, [key, val]) => str.split(key).join(val),
        text || ''
    );
}

// ── Email preview shell ───────────────────────────────────────────────────────
// s9preview-7a3d2f40 — S9b (S4 follow-up d): template text, the subject and the company name are
// ESCAPED, exactly as the server's bodyTextToHtml / buildEmailShell do. They used to go in as raw
// HTML, so a template could run script in another admin's browser and the preview could differ
// from the email actually sent.
// tmplpay-5f1d8b23 — canPayOnline picks the invoice button the real email shows (CAN_PAY /
// CANNOT_PAY .invoiceButton). It defaults to true, the can-pay wording, until the org has loaded.
export function buildPreviewHtml(subject, body, companyName, tab, canPayOnline = true) {
    const filledBody  = applyPlaceholders(body, companyName);
    const filledSubject = escapeHtml(applyPlaceholders(subject, companyName));
    const safeCompany = escapeHtml(applyPlaceholders('{companyName}', companyName));
    const bodyHtml = filledBody
        .split('\n')
        .map(line => line.trim() === '' ? '<br/>' : `<p style="margin:0 0 8px;color:#374151;">${escapeHtml(line)}</p>`)
        .join('');

    const portalButton = (tab === 'invoice' || tab === 'quote') ? `
        <div style="text-align:center;margin:20px 0;">
            <a href="#" style="display:inline-block;padding:12px 28px;background:#2563eb;color:#fff;border-radius:8px;text-decoration:none;font-weight:bold;font-size:14px;">
                ${tab === 'invoice' ? escapeHtml(invoiceEmailWordingFor(canPayOnline).button) : 'View &amp; Respond'}
            </a>
        </div>` : '';

    const lineItemsTable = (tab === 'invoice' || tab === 'quote') ? `
        <table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:13px;">
            <thead><tr style="background:#f3f4f6;">
                <th style="padding:8px 10px;text-align:left;color:#6b7280;">Item</th>
                <th style="padding:8px 10px;text-align:right;color:#6b7280;">Amount</th>
            </tr></thead>
            <tbody>
                <tr style="border-bottom:1px solid #e5e7eb;">
                    <td style="padding:8px 10px;color:#111827;">Lawn Maintenance</td>
                    <td style="padding:8px 10px;text-align:right;color:#111827;">$185.00</td>
                </tr>
                <tr style="background:#f9fafb;">
                    <td style="padding:8px 10px;font-weight:bold;color:#111827;">Total</td>
                    <td style="padding:8px 10px;text-align:right;font-weight:bold;color:#111827;">$185.00</td>
                </tr>
            </tbody>
        </table>` : '';

    return `
        <div style="font-family:Arial,sans-serif;max-width:600px;margin:auto;padding:24px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;">
            <p style="font-size:11px;color:#9ca3af;margin:0 0 16px;padding-bottom:12px;border-bottom:1px solid #e5e7eb;">
                <strong>Subject:</strong> ${filledSubject}
            </p>
            <h2 style="color:#1e40af;margin:0 0 16px;">${safeCompany}</h2>
            <hr style="border:none;border-top:1px solid #e5e7eb;margin:0 0 16px;"/>
            ${bodyHtml}
            ${portalButton}
            ${lineItemsTable}
            <hr style="border:none;border-top:1px solid #e5e7eb;margin:16px 0 8px;"/>
            <p style="color:#9ca3af;font-size:11px;text-align:center;margin:0;">Sent on behalf of ${safeCompany}</p>
        </div>
    `;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function EmailTemplatesSettings({ currentUserProfile }) {
    const orgId = currentUserProfile?.organizationId;
    const [activeTab, setActiveTab]   = useState('bookingConfirmation');
    const [templates, setTemplates]   = useState({
        bookingConfirmation: { ...DEFAULTS.bookingConfirmation },
        dayBeforeReminder:   { ...DEFAULTS.dayBeforeReminder },
        invoice:             { ...DEFAULTS.invoice },
        quote:               { ...DEFAULTS.quote },
    });
    const [companyName, setCompanyName] = useState('Your Company');
    const [docWord, setDocWord] = useState('Quote');
    // tmplpay-5f1d8b23 — can this org take online payments? Drives the invoice preview button
    // and the invoice default. Same rule as the server (orgPayments.js mirror).
    const [canPayOnline, setCanPayOnline] = useState(true);
    const [saving, setSaving]           = useState(false);
    const [savedKey, setSavedKey]       = useState('');
    const [showPreview, setShowPreview] = useState(true);
    // e1-alltabs-8b4e1c73 — which templates have unsaved edits. Save used to write
    // only the visible tab, so editing one template, switching tabs and saving
    // discarded the first edit with no warning.
    const [dirtyTabs, setDirtyTabs] = useState(() => new Set());
    // e1-loadfail-4c7a2f39 — the load had no catch, so a failed read left the
    // editor showing DEFAULTS over a real customised template; saving then
    // replaced the real one. Fail closed.
    const [loadError, setLoadError] = useState(false);
    const bodyRef = useRef(null);

    // Load saved templates + company name
    useEffect(() => {
        if (!orgId) return;
        // e1-alltabs-8b4e1c73 — reset to defaults on an org switch (God mode swaps
        // org in place), so one org's templates can't be saved into another.
        setTemplates({
            bookingConfirmation: { ...DEFAULTS.bookingConfirmation },
            dayBeforeReminder:   { ...DEFAULTS.dayBeforeReminder },
            invoice:             { ...DEFAULTS.invoice },
            quote:               { ...DEFAULTS.quote },
        });
        setDirtyTabs(new Set());
        setLoadError(false);
        getDoc(doc(db, 'organizations', orgId)).then(snap => {
            if (!snap.exists()) return;
            const data = snap.data();
            setCompanyName(data.companyName || 'Your Company');
            const word = resolveDocTerminology(data.quoteTerminologyDefault).word;
            setDocWord(word);
            const canPay = canOrgTakeOnlinePayments(data); // tmplpay-5f1d8b23
            setCanPayOnline(canPay);
            const saved = data.emailTemplates || {};
            setTemplates(prev => ({
                bookingConfirmation: saved.bookingConfirmation || prev.bookingConfirmation,
                dayBeforeReminder:   saved.dayBeforeReminder   || prev.dayBeforeReminder,
                invoice:             saved.invoice             || makeInvoiceDefault(canPay), // tmplpay-5f1d8b23
                quote:               saved.quote               || makeQuoteDefault(word),
            }));
        }).catch(err => {
            console.error('Failed to load email templates:', err);
            setLoadError(true);
        });
    }, [orgId]);

    const current = templates[activeTab];

    const updateField = (field, value) => {
        setTemplates(prev => ({
            ...prev,
            [activeTab]: { ...prev[activeTab], [field]: value },
        }));
        // e1-alltabs-8b4e1c73 — mark this tab so Save persists it even if the user
        // has since moved to another tab.
        setDirtyTabs(prev => new Set(prev).add(activeTab));
    };

    const insertPlaceholder = (placeholder) => {
        const textarea = bodyRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end   = textarea.selectionEnd;
        const val   = current.body;
        const newVal = val.substring(0, start) + placeholder + val.substring(end);
        updateField('body', newVal);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
        }, 0);
    };

    const handleSave = async () => {
        if (!orgId) return;
        // e1-loadfail-4c7a2f39 — never write templates that were never loaded.
        if (loadError) {
            alert('Templates could not be loaded, so they cannot be saved. Please reload the page.');
            return;
        }
        // e1-alltabs-8b4e1c73 — save EVERY edited tab, not just the visible one.
        // Always include the active tab so an explicit Save still writes something
        // even if nothing was flagged dirty.
        const toSave = new Set(dirtyTabs);
        toSave.add(activeTab);
        setSaving(true);
        try {
            // One dotted path per template, so saving the invoice tab can never
            // touch the quote tab — and a template this screen didn't load can
            // never be deleted.
            const payload = {};
            toSave.forEach(key => {
                if (templates[key]) payload[`emailTemplates.${key}`] = templates[key];
            });
            await updateDoc(doc(db, 'organizations', orgId), payload);
            setDirtyTabs(new Set());
            setSavedKey(activeTab);
            setTimeout(() => setSavedKey(''), 2500);
        } catch (err) {
            console.error('Failed to save template:', err);
            alert('Could not save template. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm('Reset this template to the default? Any custom changes will be lost.')) return;
        const def = activeTab === 'quote' ? makeQuoteDefault(docWord)
            : activeTab === 'invoice' ? makeInvoiceDefault(canPayOnline) // tmplpay-5f1d8b23
            : DEFAULTS[activeTab];
        updateField('subject', def.subject);
        updateField('body', def.body);
        if (!orgId) return;
        try {
            await updateDoc(doc(db, 'organizations', orgId), {
                [`emailTemplates.${activeTab}`]: def,
            });
            // e1-alltabs-8b4e1c73 — this tab now matches what's stored, so drop it
            // from the dirty set; the updateField calls above had flagged it.
            setDirtyTabs(prev => {
                const next = new Set(prev);
                next.delete(activeTab);
                return next;
            });
            setSavedKey(activeTab);
            setTimeout(() => setSavedKey(''), 2500);
        } catch (err) {
            console.error('Failed to reset template:', err);
        }
    };

    // e1-loadfail-4c7a2f39 — show an error rather than an editor full of defaults
    // that would overwrite real customised templates on save.
    if (loadError) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-bold text-red-700">Couldn't load email templates</h2>
                <p className="text-gray-600 mt-2">
                    Your saved templates could not be read, so they can't be edited right now —
                    saving would replace them with the default wording. Please reload the page, and
                    if this keeps happening, contact support.
                </p>
            </div>
        );
    }

    const previewHtml = buildPreviewHtml(current.subject, current.body, companyName, activeTab, canPayOnline);

    return (
        <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">Email Templates</h2>
            <p className="text-sm text-gray-500 mb-5">
                Customize the emails sent to your customers. Use placeholders like <code className="bg-gray-100 px-1 rounded text-xs">{'{customerName}'}</code> to personalise each message.
            </p>

            {/* Tab bar */}
            <div className="flex flex-wrap gap-1 mb-6 border-b border-gray-200">
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors -mb-px border-b-2 ${
                            activeTab === tab.key
                                ? 'border-blue-600 text-blue-700 bg-blue-50'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        {tab.label.replace('Quote', docWord)}
                    </button>
                ))}
            </div>

            <div className={`grid gap-6 ${showPreview ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
                {/* ── Editor ── */}
                <div className="space-y-4">
                    {/* Subject */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Subject Line</label>
                        <input
                            type="text"
                            value={current.subject}
                            onChange={e => updateField('subject', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            placeholder="Email subject..."
                        />
                    </div>

                    {/* Body */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Message Body</label>
                        <textarea
                            ref={bodyRef}
                            value={current.body}
                            onChange={e => updateField('body', e.target.value)}
                            rows={10}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-y"
                            placeholder="Email body..."
                        />
                        <p className="text-xs text-gray-400 mt-1">
                            {/* payhide-7e4c1b05 — since Seam 3 the invoice button reads "View Invoice
                                Online" when the business can't take online payments. Quote text unchanged. */}
                            {activeTab === 'invoice'
                                ? 'A button to your customer portal ("View & Pay Online", or "View Invoice Online" while you can\'t take online payments) and a line items table are automatically appended below your message.'
                                : activeTab === 'quote'
                                ? 'A "View & Pay" button and line items table are automatically appended below your message.'
                                : 'Your message appears in the email body. Appointment details are included automatically.'}
                        </p>
                    </div>

                    {/* Placeholder chips */}
                    <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">Insert placeholder — click to add at cursor</p>
                        <div className="flex flex-wrap gap-1.5">
                            {PLACEHOLDERS[activeTab].map(p => (
                                <button
                                    key={p.key}
                                    type="button"
                                    onClick={() => insertPlaceholder(p.key)}
                                    className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs rounded-full border border-blue-200 font-mono transition-colors"
                                    title={p.label.replace('Quote', docWord)}
                                >
                                    {p.key}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-2">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 transition-colors"
                        >
                            <Save size={14} />
                            {saving ? 'Saving…' : savedKey === activeTab ? 'Saved ✓' : 'Save'}
                        </button>
                        <button
                            onClick={handleReset}
                            className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-gray-50 text-gray-600 text-sm font-medium rounded-lg border border-gray-300 transition-colors"
                        >
                            <RotateCcw size={14} />
                            Reset to default
                        </button>
                        <button
                            onClick={() => setShowPreview(p => !p)}
                            className="ml-auto flex items-center gap-1.5 px-3 py-2 text-gray-500 hover:text-gray-700 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                        >
                            {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
                            {showPreview ? 'Hide preview' : 'Show preview'}
                        </button>
                    </div>
                </div>

                {/* ── Live Preview ── */}
                {showPreview && (
                    <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                            <Mail size={12} />
                            Live preview <span className="font-normal text-gray-400">(sample data)</span>
                        </p>
                        <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50 p-3">
                            <div
                                className="bg-white rounded-lg overflow-auto max-h-[520px] text-xs"
                                dangerouslySetInnerHTML={{ __html: previewHtml }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}